import { ref } from 'vue';

import { SHARING_TYPES } from '@/constants/data_shares';
import { useDataSharesStore } from '@/stores';

import type { PartnerSharingData } from './useSharingSettingsHub';

/**
 * Composable for managing restore to default logic in population partner sharing
 */
export function usePopulationPartnerRestore() {
  const dataSharesStore = useDataSharesStore();
  const partnerIdsToRestore = ref<Set<number>>(new Set());
  // Track partners whose overrides have been cleared via restore
  // This persists even if the partner is unmarked from restore due to new changes
  const partnerIdsWithClearedOverrides = ref<Set<number>>(new Set());

  /**
   * Mark a partner for restoration to population defaults
   * Also marks that this partner's overrides should be cleared
   */
  function markForRestore(partnerId: number) {
    partnerIdsToRestore.value = new Set(partnerIdsToRestore.value).add(
      partnerId,
    );
    partnerIdsWithClearedOverrides.value = new Set(
      partnerIdsWithClearedOverrides.value,
    ).add(partnerId);
  }

  /**
   * Remove a partner from the restore set
   */
  function unmarkForRestore(partnerId: number) {
    const newSet = new Set(partnerIdsToRestore.value);
    newSet.delete(partnerId);
    partnerIdsToRestore.value = newSet;
  }

  /**
   * Check if a partner should be removed from restore set based on new values
   */
  function shouldUnmarkForRestore(
    partnerId: number,
    populationId: number,
    currentValues: PartnerSharingData,
  ): boolean {
    if (!partnerIdsToRestore.value.has(partnerId)) return false;

    const defaultRule = dataSharesStore.getOutgoingSharingRules({
      ruleType: 'population',
      populationId,
    })[0];

    const defaultVisibility = defaultRule?.visibility ?? SHARING_TYPES.HIDDEN;
    const defaultPresetId = defaultRule?.data_share_presets_id ?? null;

    const currentVisibility = currentValues?.visibility ?? defaultVisibility;
    const currentPresetId = currentValues?.presetId ?? defaultPresetId;

    return (
      currentVisibility !== defaultVisibility ||
      currentPresetId !== defaultPresetId
    );
  }

  /**
   * Get the default visibility and preset for a population
   */
  function getPopulationDefaults(populationId: number) {
    const defaultRule = dataSharesStore.getOutgoingSharingRules({
      ruleType: 'population',
      populationId,
    })[0];

    return {
      visibility: defaultRule?.visibility ?? SHARING_TYPES.HIDDEN,
      presetId: defaultRule?.data_share_presets_id ?? null,
    };
  }

  /**
   * Clear all partners from restore set and cleared overrides tracking
   */
  function clearRestores() {
    partnerIdsToRestore.value = new Set();
    partnerIdsWithClearedOverrides.value = new Set();
  }

  return {
    partnerIdsToRestore,
    partnerIdsWithClearedOverrides,
    markForRestore,
    unmarkForRestore,
    shouldUnmarkForRestore,
    getPopulationDefaults,
    clearRestores,
  };
}
